<template>
  <div :class="side + '-sidebar'">
    <ul
      :class="['scrollable-list-' + side, { 'badges-pending': !badgesReady }]"
      ref="viewportListRef"
      :id="side === 'left' ? 'viewportList' + 'Left' : 'viewportList' + 'Right'"
      @scroll="onScroll"
    >
      <li v-for="item in store.sidebarLenses" :key="item.id">
        <a
          class="nav-title nav-entity"
          :class="side === 'left' ? 'nav-entity-left' : 'nav-entity-right'"
          >{{ item.name }}</a
        >
        <ul class="nested-list" v-if="item.lenses && item.lenses.length">
          <li
            v-for="lens in item.lenses"
            :key="lens.name"
            class="nav-item nav-entity"
            :class="side === 'left' ? 'nav-entity-left' : 'nav-entity-right'"
          >
            <a
              :ref="el => setLabelRef(lens.name, side, el)"
              :id="getLinkId(lens.name, side)"
              href="javascript:;"
              class="nav-link"
              :class="{ 'nav-link-premium': isLensLocked(lens.name) }"
              :data-premium-locked="isLensLocked(lens.name)"
              @click="setSelection(lens.name, side)"
              >{{ lens.name }}</a
            >
            <span
              :ref="el => setBadgeRef(lens.name, side, el)"
              class="badge-group"
              :class="{ 'badge-group-premium': isLensLocked(lens.name) }"
              v-if="isLensLocked(lens.name) || lens.isNew"
            >
              <span class="new-badge" v-if="lens.isNew">NEW</span>
              <img
                v-if="isLensLocked(lens.name)"
                :src="proLogo"
                alt="PRO lens"
                class="premium-badge"
                @click.stop="$emit('showPremiumAlert')"
              />
            </span>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>
<script setup>
import { useLensesStore } from '@/stores/Lenses'
import { useUsersStore } from '@/stores/Users'
import { onMounted, onUnmounted, ref, watch, nextTick, defineEmits, defineProps } from 'vue'
import { fallbackLens } from '@/includes/lens'
import proLogo from '@/assets/img/PRO_logo.webp'

const store = useLensesStore()
const userStore = useUsersStore()
const viewportListRef = ref(null)
const props = defineProps({ side: String })
const emit = defineEmits(['showPremiumAlert'])

// Every premium badge reuses this same image, so preload it once and wait for
// it to be decodable before measuring/positioning anything - otherwise the
// first positioning pass runs against a 0-width badge and visibly jumps once
// the image finishes loading.
const proLogoLoaded = new Promise(resolve => {
  const img = new Image()
  img.onload = img.onerror = resolve
  img.src = proLogo
  if (img.complete) resolve()
})

const badgesReady = ref(false)

// Scroll events can fire several times per frame - snapping does layout reads
// across the whole list, so run it at most once per animation frame
let scrollFrame = null
function onScroll() {
  if (scrollFrame) return
  scrollFrame = requestAnimationFrame(() => {
    scrollFrame = null
    store.snapToClosestOption(viewportListRef.value)
  })
}
function getLinkId(name, side) {
  // Convert to lowercase and replace spaces with hyphens
  return name.toLowerCase().replace(/\s+/g, '-') + '-' + side
}
function isLensLocked(name) {
  return userStore.isLoggedInValue && !store.isPremiumUser && store.isLensPremium(name)
}
function setSelection(name, side) {
  if (isLensLocked(name)) {
    emit('showPremiumAlert')
  }
  const id = getLinkId(name, side)
  side === 'left' ? store.setLeftSelectedVideoId(id) : store.setRightSelectedVideoId(id)
}

// Positions each lens's NEW/PRO badge-group right where its (possibly wrapped)
// label text actually ends, measured via the Range API rather than relying on
// pure CSS inline flow, since the sidebar's `overflow-x: hidden` silently clips
// anything that doesn't fit rather than reflowing it.
const labelRefs = {}
const badgeRefs = {}

function refKey(name, side) {
  return `${side}-${name}`
}
function setLabelRef(name, side, el) {
  const key = refKey(name, side)
  if (el) labelRefs[key] = el
  else delete labelRefs[key]
}
function setBadgeRef(name, side, el) {
  const key = refKey(name, side)
  if (el) badgeRefs[key] = el
  else delete badgeRefs[key]
}

const GAP = 6

// Runs in phases (all reads, then all writes) across every badge, so the
// browser lays out a handful of times total rather than several times per badge
function positionBadges(pairs) {
  const entries = pairs
    .map(([labelEl, badgeEl]) => ({
      labelEl,
      badgeEl,
      item: labelEl.closest('.nav-item'),
      badgeWidth: badgeEl.offsetWidth
    }))
    .filter(entry => entry.item)

  // Reserve trailing space on the label's last wrapped line for the badge -
  // for a `display: inline` element, right padding only applies to its final
  // line fragment, so this makes the text itself wrap a word earlier when
  // needed instead of orphaning the badge on a line of its own
  entries.forEach(({ labelEl, badgeWidth }) => {
    labelEl.style.paddingRight = `${badgeWidth + GAP}px`
  })

  const placements = entries.map(({ labelEl, badgeEl, item, badgeWidth }) => {
    const range = document.createRange()
    range.selectNodeContents(labelEl)
    const rects = range.getClientRects()
    if (!rects.length) return null

    const lastRect = rects[rects.length - 1]
    const itemRect = item.getBoundingClientRect()

    // Only possible if the badge alone is wider than the whole row - no amount
    // of reserved space helps, so fall back to dropping it below
    const wraps = badgeWidth + GAP > itemRect.width
    return {
      badgeEl,
      item,
      wraps,
      left: wraps ? 0 : lastRect.right - itemRect.left + GAP,
      top: wraps ? lastRect.bottom - itemRect.top + 4 : lastRect.top - itemRect.top,
      badgeHeight: wraps ? badgeEl.offsetHeight : 0
    }
  })

  placements.forEach(placement => {
    if (!placement) return
    const { badgeEl, item, wraps, left, top, badgeHeight } = placement
    badgeEl.style.left = `${left}px`
    badgeEl.style.top = `${top}px`
    item.style.paddingBottom = wraps ? `${badgeHeight + 4}px` : ''
  })
}

let positionFrame = null
function schedulePositionBadges(onDone) {
  if (positionFrame) cancelAnimationFrame(positionFrame)
  positionFrame = requestAnimationFrame(() => {
    const pairs = Object.keys(badgeRefs)
      .filter(key => labelRefs[key] && badgeRefs[key])
      .map(key => [labelRefs[key], badgeRefs[key]])
    positionBadges(pairs)
    if (onDone) onDone()
  })
}

// Keep the list hidden until the badge image is cached and a real positioning
// pass has run against it, so the list never flashes with badges in the wrong
// spot (or visibly jumps once they get repositioned).
async function revealWhenReady() {
  badgesReady.value = false
  await proLogoLoaded
  // Labels use custom @font-face fonts (figtree/avenir) - if they're still
  // loading, the Range API measures against the fallback font's metrics and
  // the badge visibly jumps once the real font swaps in and reflows the text.
  if (document.fonts && document.fonts.ready) {
    await document.fonts.ready
  }
  await nextTick()
  schedulePositionBadges(() => {
    badgesReady.value = true
    scrollToDefaultLens()
  })
}

// Smooth-scrolls to the selected (or default) lens once, after the badge pass has
// settled the final item heights - scrolling any earlier lands off-center because
// the badges can still change row heights.
let initialScrollDone = false
function scrollToDefaultLens() {
  if (initialScrollDone) return
  initialScrollDone = true

  const isLeft = props.side === 'left'
  const selectedId = isLeft ? store.LeftSelectedVideoId : store.RightSelectedVideoId
  const defaultId = isLeft ? store.defaultLeftVideoId : store.defaultRightVideoId
  const anchor =
    document.getElementById(selectedId) ||
    document.getElementById(defaultId) ||
    document.getElementById(fallbackLens(isLeft ? 'Left' : 'Right', store.data))
  store.scrollToItem(anchor)
}

let resizeObserver = null

onMounted(() => {
  store.addHeightOntopAndBottom(viewportListRef)

  revealWhenReady()
  resizeObserver = new ResizeObserver(() => schedulePositionBadges())
  if (viewportListRef.value) resizeObserver.observe(viewportListRef.value)
})

onUnmounted(() => {
  if (resizeObserver) resizeObserver.disconnect()
  if (positionFrame) cancelAnimationFrame(positionFrame)
  if (scrollFrame) cancelAnimationFrame(scrollFrame)
})

watch(
  () => store.data,
  () => revealWhenReady()
)
</script>

<style scoped>
.nav-link-premium {
  opacity: 0.3;
}

.active-li .nav-link-premium {
  opacity: 0.5;
}

.badge-group-premium {
  opacity: 0.3;
}

.active-li .badge-group-premium {
  opacity: 0.5;
}

.nav-item {
  position: relative;
}

/* Keeps layout intact (so clientHeight/Range measurements stay valid) while
   the badge positions are computed, instead of display:none which would
   zero those out. */
.badges-pending {
  visibility: hidden;
}

.nav-link {
  display: inline;
  white-space: normal;
  overflow-wrap: anywhere;
}

.badge-group {
  position: absolute;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0.3rem;
  /* left/top are set from JS once the label's actual end position is measured */
}

.premium-badge {
  display: inline-block;
  height: 0.9rem;
  width: auto;
  flex-shrink: 0;
  vertical-align: middle;
  cursor: pointer;
  opacity: 0.85;
}

.badge-group .new-badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 0.85rem;
  margin-left: 0;
  margin-top: 0.2rem;
  padding: 0.5rem 0.3rem 0.4rem;
  font-size: 0.7rem;
  line-height: 1;
  box-sizing: border-box;
}
</style>
